export interface DateValue {
  date: string;
  value: number;
}

export interface TopPost {
  id: string;
  caption: string;
  likes: number;
  comments: number;
  reach: number;
  engagementRate: number;
  type: 'image' | 'video' | 'reel';
  postedAt: string;
}

export interface Story {
  date: string;
  views: number;
  replies: number;
  impressions: number;
}

export interface AudienceData {
  gender: Array<{ label: string; value: number }>;
  ageRanges: Array<{ range: string; value: number }>;
  topCountries: Array<{ country: string; value: number }>;
  topCities: Array<{ city: string; value: number }>;
  activeHours: Array<{ hour: number; value: number }>;
  contentPreferences: Array<{ type: string; value: number }>;
}

export interface InstagramData {
  followers: DateValue[];
  reach: DateValue[];
  impressions: DateValue[];
  engagementRate: DateValue[];
  topPosts: TopPost[];
  stories: Story[];
  audience: AudienceData;
}

class InstagramService {
  private baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  async getInstagramData(): Promise<InstagramData> {
    try {
      console.log('🔄 Fetching Instagram data from:', `${this.baseURL}/api/instagram`);
      
      const response = await fetch(`${this.baseURL}/api/instagram`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('📊 Instagram response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: InstagramData = await response.json();
      console.log('✅ Instagram data received:', data);
      
      return data;
    } catch (error) {
      console.error('❌ Error fetching Instagram data:', error);
      
      // Fallback to mock data if API fails
      console.log('🔄 Falling back to mock Instagram data');
      return this.getMockInstagramData();
    }
  }

  private getMockInstagramData(): InstagramData {
    // Generate 30 days of continuous data
    const generateDateValueArray = (baseValue: number, variance: number = 0.2): DateValue[] => {
      const data: DateValue[] = [];
      for (let i = 0; i < 30; i++) {
        const date = new Date(2025, 0, i + 1).toISOString().split('T')[0];
        const randomFactor = 1 + (Math.random() - 0.5) * variance;
        data.push({
          date,
          value: Math.round(baseValue * randomFactor)
        });
      }
      return data;
    };

    return {
      followers: generateDateValueArray(5000, 0.1), // Slower growth
      reach: generateDateValueArray(15000, 0.3),
      impressions: generateDateValueArray(25000, 0.25),
      engagementRate: generateDateValueArray(3.5, 0.4), // Percentage
      topPosts: [
        {
          id: '1',
          caption: 'Novo produto lançado! 🚀',
          likes: 1250,
          comments: 89,
          reach: 8500,
          engagementRate: 4.2,
          type: 'image',
          postedAt: '2025-01-15T10:30:00Z'
        },
        {
          id: '2',
          caption: 'Behind the scenes do nosso processo criativo ✨',
          likes: 980,
          comments: 67,
          reach: 7200,
          engagementRate: 3.8,
          type: 'video',
          postedAt: '2025-01-12T14:15:00Z'
        },
        {
          id: '3',
          caption: 'Dica do dia: Como otimizar suas campanhas 📈',
          likes: 1100,
          comments: 45,
          reach: 6800,
          engagementRate: 3.2,
          type: 'reel',
          postedAt: '2025-01-10T16:45:00Z'
        },
        {
          id: '4',
          caption: 'Resultados incríveis dos nossos clientes 🎯',
          likes: 890,
          comments: 34,
          reach: 5900,
          engagementRate: 2.9,
          type: 'image',
          postedAt: '2025-01-08T11:20:00Z'
        },
        {
          id: '5',
          caption: 'Workshop gratuito - Inscreva-se já! 📚',
          likes: 750,
          comments: 56,
          reach: 5200,
          engagementRate: 3.1,
          type: 'video',
          postedAt: '2025-01-05T13:30:00Z'
        }
      ],
      stories: generateDateValueArray(800, 0.3).map((item) => ({
        date: item.date,
        views: item.value,
        replies: Math.round(item.value * 0.15),
        impressions: Math.round(item.value * 1.2)
      })),
      audience: {
        gender: [
          { label: 'Male', value: 55 },
          { label: 'Female', value: 43 },
          { label: 'Unknown', value: 2 }
        ],
        ageRanges: [
          { range: '13-17', value: 5 },
          { range: '18-24', value: 25 },
          { range: '25-34', value: 40 },
          { range: '35-44', value: 20 },
          { range: '45+', value: 10 }
        ],
        topCountries: [
          { country: 'Brazil', value: 60 },
          { country: 'USA', value: 20 },
          { country: 'Portugal', value: 10 },
          { country: 'Other', value: 10 }
        ],
        topCities: [
          { city: 'São Paulo', value: 25 },
          { city: 'Rio de Janeiro', value: 15 },
          { city: 'New York', value: 10 },
          { city: 'Belo Horizonte', value: 8 },
          { city: 'Brasília', value: 7 }
        ],
        activeHours: [
          { hour: 8, value: 50 },
          { hour: 9, value: 75 },
          { hour: 10, value: 90 },
          { hour: 11, value: 85 },
          { hour: 12, value: 100 },
          { hour: 13, value: 95 },
          { hour: 14, value: 80 },
          { hour: 15, value: 85 },
          { hour: 16, value: 90 },
          { hour: 17, value: 95 },
          { hour: 18, value: 120 },
          { hour: 19, value: 110 },
          { hour: 20, value: 100 },
          { hour: 21, value: 80 },
          { hour: 22, value: 60 },
          { hour: 23, value: 40 }
        ],
        contentPreferences: [
          { type: 'Reels', value: 50 },
          { type: 'Stories', value: 30 },
          { type: 'Posts', value: 20 }
        ]
      }
    };
  }
}

export const instagramService = new InstagramService(); 